import { ArrowRight, CalendarDays, Check, ChevronRight, Mail, MapPin, Phone, Search, Sparkles } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { companyInfo, designTokens } from '../data/siteData'
import { cn } from '../lib/utils'

export function SectionHeading({ eyebrow, title, description, align = 'left', dark = false }) {
  return (
    <div className={cn('max-w-3xl', align === 'center' && 'mx-auto text-center')}>
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#3B70E5]">{eyebrow}</p>
      ) : null}
      <h2 className={cn('mt-3 text-3xl font-bold tracking-tight sm:text-4xl', dark ? 'text-white' : 'text-slate-950')}>{title}</h2>
      {description ? <div className={cn('mt-4 text-base leading-7', dark ? 'text-slate-300' : 'text-slate-600')}>{description}</div> : null}
    </div>
  )
}

export function Pill({ children, tone = 'light' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold',
        tone === 'light' && 'bg-white text-slate-700 shadow-sm ring-1 ring-slate-200',
        tone === 'dark' && 'bg-slate-900 text-white ring-1 ring-white/10',
        tone === 'lime' && 'bg-[#3B70E5] text-white',
      )}
    >
      {children}
    </span>
  )
}

export function HeroBanner({ title, subtitle, eyebrow, image = designTokens.heroImage, ctas = [], compact = false }) {
  return (
    <section className="relative overflow-hidden bg-slate-950 text-white max-h-[450px]">
      <div className="absolute inset-0">
        <img src={image} alt="" className="h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-slate-950/55" />
      </div>
      <div className={cn('relative mx-auto grid max-w-7xl items-center px-4 py-6 sm:px-6 lg:px-8', compact ? 'min-h-[300px]' : 'min-h-[450px] max-h-[450px]')}>
        <div className={cn('mx-auto flex max-w-4xl flex-col items-center text-center justify-center', compact ? 'pb-2 pt-4 sm:pt-8' : 'pb-3 pt-4 sm:pt-8')}>
          {eyebrow ? (
            <Pill tone="lime">
              <Sparkles className="h-3 w-3" />
              {eyebrow}
            </Pill>
          ) : null}
          <h1 className="mt-3 max-w-4xl text-xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">
            {title}
          </h1>
          {subtitle ? <div className="mt-3 max-w-2xl text-[11px] leading-5 text-slate-200 sm:text-sm sm:leading-6">{subtitle}</div> : null}
          {ctas.length ? (
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              {ctas.map((cta) => {
                const isExternal = /^(https?:|mailto:|tel:)/.test(cta.href)
                const className = cn(
                  'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition',
                  cta.primary
                    ? 'bg-[#3B70E5] text-white hover:bg-[#3B70E5]/90'
                    : 'border border-white/15 bg-white/10 text-white hover:bg-white/20',
                )

                if (isExternal) {
                  return (
                    <a
                      key={cta.label}
                      href={cta.href}
                      target={cta.href.startsWith('http') ? '_blank' : undefined}
                      rel={cta.href.startsWith('http') ? 'noreferrer' : undefined}
                      className={className}
                    >
                      {cta.label}
                      <ChevronRight className="h-4 w-4" />
                    </a>
                  )
                }

                return (
                  <Link key={cta.label} to={cta.href} className={className}>
                    {cta.label}
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                )
              })}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

export function SplitFeature({ title, description, image, cta, reverse = false }) {
  return (
    <section className="bg-stone-100 py-16 sm:py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div className={cn(reverse && 'order-2 lg:order-1')}>
          <SectionHeading eyebrow="Fokus" title={title} description={description} />
          {cta ? (
            <div className="mt-8">
              <Link
                to={cta.href}
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                {cta.label}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : null}
        </div>
        <div className={cn('overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm', reverse && 'order-1 lg:order-2')}>
          <img src={image} alt="" className="h-full w-full object-cover" />
        </div>
      </div>
    </section>
  )
}

export function CardGrid({ title, description, items, columns = 3 }) {
  const gridClass = columns === 2 ? 'lg:grid-cols-2' : columns === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
  return (
    <section className="py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Udvalgt" title={title} description={description} />
        <div className={cn('mt-10 grid gap-6 sm:grid-cols-2', gridClass)}>
          {items.map((item) => (
            <Link
              key={item.title}
              to={item.href}
              className="group overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
            >
              <div className="aspect-[4/3] overflow-hidden bg-slate-200">
                <img
                  src={item.image}
                  alt={item.title}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </div>
              <div className="space-y-3 p-5">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#3B70E5]">
                  <CalendarDays className="h-4 w-4" />
                  {item.kicker}
                </div>
                <h3 className="text-xl font-bold tracking-tight text-slate-950">{item.title}</h3>
                <p className="text-sm leading-6 text-slate-600">{item.text}</p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition group-hover:text-[#3B70E5]">
                  Læs mere
                  <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

export function ArticleShell({ title, description, image, imagePosition = '50% 27%', children, meta }) {
  return (
    <article className="py-12 sm:py-16">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {meta}
        <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
          {image ? (
            <div className="border-b border-slate-200 bg-slate-100">
              <img src={image} alt="" className="aspect-[16/7] w-full object-cover" style={{ objectPosition: imagePosition }} />
            </div>
          ) : null}
          <div className="p-6 sm:p-10">
            <h1 className="text-3xl font-bold tracking-tight text-slate-950 sm:text-5xl">{title}</h1>
            {description ? <p className="mt-4 max-w-3xl text-base leading-7 text-slate-600">{description}</p> : null}
            <div className="mt-8">{children}</div>
          </div>
        </div>
      </div>
    </article>
  )
}

export function RichText({ html }) {
  const processedHtml = useMemo(() => {
    if (!html) return ''
    
    if (typeof window !== 'undefined') {
      try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        
        // Extract scripts and replace with placeholders
        const scripts = Array.from(doc.querySelectorAll('script'))
        scripts.forEach((script) => {
          const src = script.getAttribute('src')
          if (src) {
            const placeholder = doc.createElement('div')
            placeholder.className = 'conventus-script-placeholder my-6'
            placeholder.setAttribute('data-src', src)
            script.parentNode.replaceChild(placeholder, script)
          }
        })

        const paragraphs = Array.from(doc.querySelectorAll('p'))
        
        // First pass: identify headings and obvious address lines
        paragraphs.forEach((p, index) => {
          const text = p.textContent.trim()
          const isHeading = p.children.length === 1 && p.children[0].tagName === 'STRONG' && p.childNodes.length === 1
          
          if (isHeading) {
            p.setAttribute('data-rt-type', 'heading')
          } else {
            const isShort = text.length < 50
            const hasZip = /\b\d{4}\b/.test(text)
            const hasStreetNumber = /\b\d+\b/.test(text)
            
            if (isShort && (hasZip || hasStreetNumber)) {
              p.setAttribute('data-rt-type', 'address-line')
            }
          }
        })
        
        // Second pass: propagate address-line to adjacent short paragraphs
        paragraphs.forEach((p, index) => {
          if (p.getAttribute('data-rt-type')) return
          
          const text = p.textContent.trim()
          const isShort = text.length < 50
          
          if (isShort) {
            const prevType = paragraphs[index - 1]?.getAttribute('data-rt-type')
            const nextType = paragraphs[index + 1]?.getAttribute('data-rt-type')
            
            if (prevType === 'heading' || prevType === 'address-line' || nextType === 'address-line') {
              p.setAttribute('data-rt-type', 'address-line')
            }
          }
        })
        
        // Set default for remaining
        paragraphs.forEach((p) => {
          if (!p.getAttribute('data-rt-type')) {
            p.setAttribute('data-rt-type', 'paragraph')
          }
        })
        
        return doc.body.innerHTML
      } catch (e) {
        console.error('Error parsing rich text HTML:', e)
        return html
      }
    }
    
    return html
  }, [html])

  useEffect(() => {
    const containers = document.querySelectorAll('.conventus-script-placeholder')
    containers.forEach((container) => {
      if (container.getAttribute('data-loaded') === 'true') return
      container.setAttribute('data-loaded', 'true')
      
      const src = container.getAttribute('data-src')
      
      // Create a loading spinner
      const loader = document.createElement('div')
      loader.className = 'conventus-loader flex flex-col items-center justify-center py-12 text-slate-500'
      loader.innerHTML = `
        <i class="fa-solid fa-spinner fa-spin text-3xl text-[#3B70E5] mb-3"></i>
        <p class="text-sm font-medium">Henter holdoversigt fra Conventus...</p>
      `
      container.appendChild(loader)
      
      // Create iframe for safe execution of external scripts (especially those using document.write)
      const iframe = document.createElement('iframe')
      iframe.style.width = '100%'
      iframe.style.border = 'none'
      iframe.style.overflow = 'hidden'
      iframe.style.height = '0px' // start hidden, show when loaded
      iframe.style.opacity = '0'
      iframe.style.transition = 'opacity 0.3s ease'
      iframe.setAttribute('scrolling', 'no')
      
      container.appendChild(iframe)
      
      try {
        const iframeDoc = iframe.contentWindow.document || iframe.contentDocument
        if (iframeDoc) {
          iframeDoc.open()
          iframeDoc.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <meta charset="utf-8">
                <style>
                  body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; }
                  #conventus-wrapper { min-height: 100px; }
                </style>
              </head>
              <body>
                <div id="conventus-wrapper">
                  <script type="text/javascript" src="${src}"></script>
                </div>
                <script>
                  function sendHeight() {
                    setTimeout(() => {
                      const wrapper = document.getElementById('conventus-wrapper');
                      const height = wrapper ? wrapper.offsetHeight : document.body.scrollHeight;
                      window.parent.postMessage({ type: 'resize-conventus', height: height }, '*');
                    }, 200);
                  }
                  window.onload = function() {
                    sendHeight();
                    // Setup observer for dynamic content changes
                    const observer = new MutationObserver(sendHeight);
                    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
                  };
                </script>
              </body>
            </html>
          `)
          iframeDoc.close()
        }
      } catch (err) {
        console.error('Error writing to iframe:', err)
        loader.innerHTML = `<p class="text-sm text-red-500">Kunne ikke indlæse holdoversigt. Prøv at genindlæse siden.</p>`
      }
    })

    const handleMessage = (event) => {
      if (event.data && event.data.type === 'resize-conventus') {
        const containers = document.querySelectorAll('.conventus-script-placeholder')
        containers.forEach((container) => {
          const loader = container.querySelector('.conventus-loader')
          if (loader) {
            loader.remove() // remove loader when content is received
          }
          
          const iframe = container.querySelector('iframe')
          if (iframe) {
            iframe.style.height = (event.data.height + 40) + 'px'
            iframe.style.opacity = '1'
          }
        })
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [processedHtml])

  return (
    <div className="rich-text-container text-base leading-relaxed text-slate-800">
      <style>{`
        /* Default paragraphs / fallback */
        .rich-text-container p:not([data-rt-type]) {
          margin-bottom: 1.25rem !important;
          font-size: 1rem !important;
          line-height: 1.625 !important;
          color: #334155 !important;
        }

        /* Normal paragraphs: beautiful spacing and line height */
        .rich-text-container p[data-rt-type="paragraph"] {
          margin-bottom: 1.25rem !important;
          font-size: 1rem !important;
          line-height: 1.625 !important;
          color: #334155 !important; /* text-slate-700 */
        }
        .rich-text-container p[data-rt-type="paragraph"]:last-child {
          margin-bottom: 0 !important;
        }
        
        /* Address lines: tight spacing for address details */
        .rich-text-container p[data-rt-type="address-line"] {
          margin-bottom: 0.25rem !important;
          font-size: 1rem !important;
          line-height: 1.525 !important;
          color: #334155 !important; /* text-slate-700 */
        }
        
        /* Heading paragraphs (paragraphs containing only a strong tag) */
        .rich-text-container p[data-rt-type="heading"] {
          display: block !important;
          margin-top: 2rem !important;
          margin-bottom: 0.5rem !important;
        }
        .rich-text-container p[data-rt-type="heading"] > strong {
          display: inline-block !important;
          font-size: 1.25rem !important; /* text-xl */
          font-weight: 800 !important; /* font-extrabold */
          color: #0f172a !important; /* text-slate-950 */
          letter-spacing: -0.025em !important;
        }
        
        /* Headings */
        .rich-text-container h1 {
          font-size: 1.875rem !important; /* text-3xl */
          font-weight: 800 !important;
          color: #0f172a !important;
          margin-top: 2.5rem !important;
          margin-bottom: 1rem !important;
          display: block !important;
          letter-spacing: -0.025em !important;
        }
        .rich-text-container h2 {
          font-size: 1.5rem !important; /* text-2xl */
          font-weight: 800 !important;
          color: #0f172a !important;
          margin-top: 2rem !important;
          margin-bottom: 1rem !important;
          display: block !important;
          letter-spacing: -0.025em !important;
        }
        .rich-text-container h3 {
          font-size: 1.25rem !important; /* text-xl */
          font-weight: 800 !important;
          color: #0f172a !important;
          margin-top: 2rem !important;
          margin-bottom: 1rem !important;
          display: block !important;
          letter-spacing: -0.025em !important;
        }
        .rich-text-container h4 {
          font-size: 1.125rem !important; /* text-lg */
          font-weight: 700 !important;
          color: #0f172a !important;
          margin-top: 1.5rem !important;
          margin-bottom: 0.75rem !important;
          display: block !important;
          letter-spacing: -0.025em !important;
        }
        
        /* Lists */
        .rich-text-container ul {
          list-style-type: disc !important;
          margin-top: 1rem !important;
          margin-bottom: 1.25rem !important;
          padding-left: 1.5rem !important;
        }
        .rich-text-container ul > li {
          margin-bottom: 0.5rem !important;
          color: #334155 !important;
          line-height: 1.625 !important;
        }
        .rich-text-container ol {
          list-style-type: decimal !important;
          margin-top: 1rem !important;
          margin-bottom: 1.25rem !important;
          padding-left: 1.5rem !important;
        }
        .rich-text-container ol > li {
          margin-bottom: 0.5rem !important;
          color: #334155 !important;
          line-height: 1.625 !important;
        }
        
        /* Links */
        .rich-text-container a {
          font-weight: 600 !important;
          color: #3B70E5 !important;
          text-decoration: underline !important;
          text-underline-offset: 4px !important;
          transition: color 0.2s !important;
        }
        .rich-text-container a:hover {
          color: rgba(59, 112, 229, 0.8) !important;
        }
        
        /* Inline formatting */
        .rich-text-container strong {
          font-weight: 700 !important;
          color: #0f172a !important;
        }
        .rich-text-container em {
          font-style: italic !important;
          color: #475569 !important; /* text-slate-600 */
        }
      `}</style>
      <div dangerouslySetInnerHTML={{ __html: processedHtml || '' }} />
    </div>
  )
}

export function DetailMeta({ items }) {
  return (
    <div className="mb-6 flex flex-wrap gap-3">
      {items.filter(Boolean).map((item) => (
        <Pill key={item} tone="dark">
          <Check className="h-3.5 w-3.5 text-blue-300" />
          {item}
        </Pill>
      ))}
    </div>
  )
}

export function FeatureList({ items }) {
  const hasSingleItemOnLastRow = items.length % 3 === 1

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-12">
      {items.map((item, index) => {
        const isLastSingleItem = hasSingleItemOnLastRow && index === items.length - 1

        return (
          <div
            key={item.title}
            className={cn(
              'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-4',
              isLastSingleItem && 'lg:col-span-12',
            )}
          >
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#3B70E5]/10 text-[#3B70E5]">
                {item.icon}
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">{item.text}</p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function ContactInfoStrip() {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <a className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#3B70E5] hover:shadow-md" href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`}>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Telefon</p>
          <p className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-950">{companyInfo.phone}</p>
        </div>
        <Phone className="h-5 w-5 text-[#3B70E5]" />
      </a>
      <a className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#3B70E5] hover:shadow-md" href={`mailto:${companyInfo.email}`}>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Email</p>
          <p className="mt-1 whitespace-nowrap text-sm font-semibold text-slate-950">{companyInfo.email}</p>
        </div>
        <Mail className="h-5 w-5 text-[#3B70E5]" />
      </a>
      <a className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-[#3B70E5] hover:shadow-md" href={companyInfo.facebook} target="_blank" rel="noreferrer">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Facebook</p>
          <p className="mt-1 text-sm font-semibold text-slate-950">Følg foreningen</p>
        </div>
        <Search className="h-5 w-5 text-[#3B70E5]" />
      </a>
    </div>
  )
}

export function GalleryGrid({ images }) {
  const [activeIndex, setActiveIndex] = useState(null)
  const thumbs = useMemo(() => images.slice(0, 4), [images])
  const activeImage = activeIndex !== null ? thumbs[activeIndex] : null

  const goToNext = () => {
    setActiveIndex((current) => {
      if (current === null) return 0
      return (current + 1) % thumbs.length
    })
  }

  const goToPrevious = () => {
    setActiveIndex((current) => {
      if (current === null) return 0
      return (current - 1 + thumbs.length) % thumbs.length
    })
  }

  useEffect(() => {
    if (activeIndex === null) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setActiveIndex(null)
      }
      if (event.key === 'ArrowRight') {
        setActiveIndex((current) => (current === null ? 0 : (current + 1) % thumbs.length))
      }
      if (event.key === 'ArrowLeft') {
        setActiveIndex((current) => (current === null ? 0 : (current - 1 + thumbs.length) % thumbs.length))
      }
    }

    window.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = ''
    }
  }, [activeIndex, thumbs.length])

  if (!thumbs.length) return null

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {thumbs.map((image, index) => (
          <button
            key={image}
            type="button"
            onClick={() => setActiveIndex(index)}
            className="block h-56 overflow-hidden rounded-none border-0 bg-transparent p-0 text-left shadow-none transition hover:opacity-90 sm:h-64 lg:h-56"
            aria-label={`Open image ${index + 1}`}
          >
            <img src={image} alt="" className="block h-full w-full object-cover" />
          </button>
        ))}
      </div>

      {activeImage ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setActiveIndex(null)}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20"
            onClick={(event) => {
              event.stopPropagation()
              setActiveIndex(null)
            }}
          >
            Close
          </button>

          <button
            type="button"
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20"
            onClick={(event) => {
              event.stopPropagation()
              goToPrevious()
            }}
            aria-label="Previous image"
          >
            <ChevronRight className="h-5 w-5 rotate-180" />
          </button>

          <button
            type="button"
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20"
            onClick={(event) => {
              event.stopPropagation()
              goToNext()
            }}
            aria-label="Next image"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <img
            src={activeImage}
            alt=""
            className="max-h-[90vh] max-w-[95vw] object-contain shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </>
  )
}
